import { ICommand } from './ICommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { Node, Edge } from '../models/scene';
import type * as Y from 'yjs';
import { validateShapeLimitOrPromptUpgrade } from '../../utils/shapeLimitUtils';
import { getNodeGroupPath } from '../../lib/diagram/grouping';
import { ShapeMapAdapter } from '../../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../../lib/diagram/shapeGeometry';
import { snapPointToGrid } from '../../lib/diagram/gridSnap';
import { getTableDropHitAtPoint } from '../../domains/diagram/shapes/table/tableDrop.utils';
import {
  CLIPBOARD_FROM_POINT_KEY,
  CLIPBOARD_TO_POINT_KEY,
  stripClipboardEndpointPoints,
  type ClipboardPoint,
} from '../clipboard/clipboardSelection';

type TableDock = { tableId: string; cellKey: `${number},${number}`; offset?: { x: number; y: number } };

type PasteMutationManager = {
  getDoc?: () => Y.Doc | undefined;
  ydoc?: Y.Doc;
  addNode: (node: Node) => unknown;
  addEdge: (edge: Edge) => unknown;
  deleteNode: (nodeId: string) => unknown;
  deleteEdge: (edgeId: string) => unknown;
  batchAddNodes?: (nodes: Node[]) => unknown;
  batchAddEdges?: (edges: Edge[]) => unknown;
  batchDeleteNodes?: (nodeIds: string[]) => unknown;
  batchDeleteEdges?: (edgeIds: string[]) => unknown;
};

const getNodeSize = (node: Node): { w: number; h: number } => ({
  w: Number(node.w ?? node.width ?? 60) || 60,
  h: Number(node.h ?? node.height ?? 60) || 60,
});

const resolvePastedTableDock = (
  node: Node,
  attrs: Record<string, unknown>,
  allNodes: Node[],
): Record<string, unknown> => {
  const dock = attrs.tableDock as TableDock | undefined;
  if (!dock?.tableId) return attrs;

  const nextAttrs = { ...attrs };
  const tableNode = allNodes.find((candidate) => candidate.id === dock.tableId && candidate.type === 'table');
  if (!tableNode) {
    delete nextAttrs.tableDock;
    return nextAttrs;
  }

  const size = getNodeSize(node);
  const hit = getTableDropHitAtPoint(tableNode, {
    x: node.x + size.w / 2,
    y: node.y + size.h / 2,
  });

  if (!hit) {
    delete nextAttrs.tableDock;
    return nextAttrs;
  }

  nextAttrs.tableDock = {
    ...dock,
    cellKey: hit.cellKey,
    offset: {
      x: node.x - hit.contentRect.x,
      y: node.y - hit.contentRect.y,
    },
  };
  return nextAttrs;
};

export class PasteNodesCommand implements ICommand {
  id: string;
  name = 'Paste Nodes';
  description = 'Paste nodes from clipboard';
  timestamp = Date.now();

  private pastedNodes: Node[] = [];
  private pastedEdges: Edge[] = [];
  private sourceNodes: Node[];
  private sourceEdges: Edge[];
  private offset: { x: number; y: number };

  constructor(source: { nodes: Node[]; edges: Edge[] }, offset: { x: number; y: number } = { x: 20, y: 20 }) {
    this.id = `paste-nodes-${Date.now()}`;
    this.sourceNodes = source.nodes;
    this.sourceEdges = source.edges;
    this.offset = offset;
  }

  private isPoint(value: unknown): value is ClipboardPoint {
    if (!value || typeof value !== 'object') return false;
    const point = value as { x?: unknown; y?: unknown };
    return typeof point.x === 'number' && Number.isFinite(point.x) && typeof point.y === 'number' && Number.isFinite(point.y);
  }

  private resolveClipboardPoint(edge: Edge, endpoint: 'from' | 'to'): ClipboardPoint {
    const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
    const clipboardPoint = endpoint === 'from' ? attrs[CLIPBOARD_FROM_POINT_KEY] : attrs[CLIPBOARD_TO_POINT_KEY];
    if (this.isPoint(clipboardPoint)) return clipboardPoint;

    const freePoint = endpoint === 'from' ? attrs.fromPoint : attrs.toPoint;
    if (this.isPoint(freePoint)) return freePoint;
    return { x: 0, y: 0 };
  }

  private translatePoint(point: ClipboardPoint): ClipboardPoint {
    return {
      x: point.x + this.offset.x,
      y: point.y + this.offset.y,
    };
  }

  private offsetPoint(point: ClipboardPoint): ClipboardPoint {
    return snapPointToGrid({
      ...this.translatePoint(point),
    });
  }

  private offsetFlatPointArray(value: unknown): number[] | undefined {
    if (!Array.isArray(value) || value.length < 2 || value.length % 2 !== 0) return undefined;
    const shifted: number[] = [];
    for (let i = 0; i < value.length; i += 2) {
      const x = Number(value[i]);
      const y = Number(value[i + 1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
      shifted.push(x + this.offset.x, y + this.offset.y);
    }
    return shifted;
  }

  private offsetWaypointArray(value: unknown): Array<Record<string, unknown>> | undefined {
    if (!Array.isArray(value)) return undefined;
    const shifted: Array<Record<string, unknown>> = [];
    for (const waypoint of value) {
      if (!waypoint || typeof waypoint !== 'object') return undefined;
      const record = waypoint as Record<string, unknown>;
      const x = Number(record.x);
      const y = Number(record.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
      shifted.push({
        ...record,
        x: x + this.offset.x,
        y: y + this.offset.y,
      });
    }
    return shifted;
  }

  private offsetPersistedRouteGeometry(edge: Edge, attrs: Record<string, unknown>): Record<string, unknown> {
    const nextEdge: Record<string, unknown> = {};
    const shiftedLayoutPoints = this.offsetFlatPointArray(attrs.layoutPoints);
    if (shiftedLayoutPoints) attrs.layoutPoints = shiftedLayoutPoints;

    const shiftedAttrWaypoints = this.offsetWaypointArray(attrs.waypoints);
    if (shiftedAttrWaypoints) attrs.waypoints = shiftedAttrWaypoints;

    const edgeRecord = edge as Edge & Record<string, unknown>;
    const shiftedPoints = this.offsetFlatPointArray(edgeRecord.points);
    if (shiftedPoints) nextEdge.points = shiftedPoints;

    const shiftedWaypoints = this.offsetWaypointArray(edgeRecord.waypoints);
    if (shiftedWaypoints) nextEdge.waypoints = shiftedWaypoints;

    return nextEdge;
  }

  execute(): void {
    const store = useDiagramStore.getState();
    const mutationManager = getNodeMutationManager() as unknown as PasteMutationManager;

    console.log('[PasteNodesCommand] Starting paste operation:', {
      nodeCount: this.sourceNodes.length,
      edgeCount: this.sourceEdges.length,
      offset: this.offset
    });
    
    // Check shape limits
    // Paste can create multiple nodes + edges at once
    const shapesToAdd = this.sourceNodes.length + this.sourceEdges.length;
    if (!validateShapeLimitOrPromptUpgrade(store, shapesToAdd)) {
      return; 
    }

    const idMap = new Map<string, string>();
    const groupIdMap = new Map<string, string>();

    this.sourceNodes.forEach((node) => {
      const path = getNodeGroupPath(node);
      path.forEach((groupId) => {
        if (!groupIdMap.has(groupId)) {
          groupIdMap.set(groupId, `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
        }
      });
    });

    this.pastedNodes = this.sourceNodes.map(node => {
      const newId = `${node.id}-paste-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      idMap.set(node.id, newId);
      let nextAttrs = node.attrs ? { ...node.attrs } : undefined;
      const path = getNodeGroupPath(node);
      if (path.length > 0) {
        if (!nextAttrs) nextAttrs = {};
        nextAttrs.groupPath = path.map((groupId) => groupIdMap.get(groupId) || groupId);
      }
      const pastedNode = {
        ...node,
        id: newId,
        ...snapPointToGrid({
          x: (node.x || 0) + this.offset.x,
          y: (node.y || 0) + this.offset.y,
        }),
        attrs: nextAttrs
      } as Node;
      if (pastedNode.attrs) {
        pastedNode.attrs = resolvePastedTableDock(pastedNode, pastedNode.attrs, store.nodes);
      }
      return pastedNode;
    });

    this.pastedEdges = this.sourceEdges.map(edge => {
      const newId = `${edge.id}-paste-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const from = idMap.get(edge.from);
      const to = idMap.get(edge.to);
      const cleanEdge = stripClipboardEndpointPoints(edge);
      const attrs = { ...(cleanEdge.attrs ?? {}) } as Record<string, unknown>;
      const shouldTranslateAsFreeGeometry = !from && !to;
      const translatedRouteGeometry = shouldTranslateAsFreeGeometry
        ? this.offsetPersistedRouteGeometry(cleanEdge, attrs)
        : {};
      const pastedEdge: Edge = {
        ...cleanEdge,
        ...translatedRouteGeometry,
        id: newId,
        from: from ?? `__freeF:${newId}`,
        to: to ?? `__freeT:${newId}`,
        attrs,
      } as Edge;

      if (!from) {
        attrs.fromPoint = shouldTranslateAsFreeGeometry
          ? this.translatePoint(this.resolveClipboardPoint(edge, 'from'))
          : this.offsetPoint(this.resolveClipboardPoint(edge, 'from'));
      }
      if (!to) {
        attrs.toPoint = shouldTranslateAsFreeGeometry
          ? this.translatePoint(this.resolveClipboardPoint(edge, 'to'))
          : this.offsetPoint(this.resolveClipboardPoint(edge, 'to'));
      }

      return pastedEdge;
    });

    // Batch add nodes and edges if multiple
    if (this.pastedNodes.length > 1) {
      if (mutationManager.batchAddNodes) {
        mutationManager.batchAddNodes(this.pastedNodes);
      } else {
        this.pastedNodes.forEach((node) => mutationManager.addNode(node));
      }
    } else if (this.pastedNodes.length === 1) {
      mutationManager.addNode(this.pastedNodes[0]);
    }
    
    if (this.pastedEdges.length > 1) {
      if (mutationManager.batchAddEdges) {
        mutationManager.batchAddEdges(this.pastedEdges);
      } else {
        this.pastedEdges.forEach((edge) => mutationManager.addEdge(edge));
      }
    } else if (this.pastedEdges.length === 1) {
      mutationManager.addEdge(this.pastedEdges[0]);
    }
    store.setSelected(new Set(this.pastedNodes.length > 0 ? this.pastedNodes.map(n => n.id) : this.pastedEdges.map(e => e.id)));

    console.log('[PasteNodesCommand] Paste completed:', {
      pastedNodeIds: this.pastedNodes.map(n => n.id),
      pastedEdgeIds: this.pastedEdges.map(e => e.id)
    });
  }

  undo(): void {
    const store = useDiagramStore.getState();
    const mutationManager = getNodeMutationManager() as unknown as PasteMutationManager;
    
    // Delete edges and nodes in a SINGLE combined transaction to avoid multiple requests
    const ydoc = mutationManager.getDoc?.() ?? mutationManager.ydoc;
    if (ydoc && (this.pastedEdges.length > 0 || this.pastedNodes.length > 0)) {
      const shapes = new ShapePersistenceGateway(new ShapeMapAdapter(ydoc.getMap('shapes')));
      const connectorsMap = ydoc.getMap('connectors');
      
      ydoc.transact(() => {
        this.pastedEdges.forEach(edge => {
          connectorsMap.delete(edge.id);
        });
        
        this.pastedNodes.forEach(node => {
          shapes.delete(node.id);
        });
      }, 'local');
      
      // DO NOT update store here - let Yjs → Store sync handle it
    } else {
      // No Yjs doc - use batch methods
      if (this.pastedEdges.length > 1) {
        if (mutationManager.batchDeleteEdges) {
          mutationManager.batchDeleteEdges(this.pastedEdges.map(e => e.id));
        } else {
          this.pastedEdges.forEach((edge) => mutationManager.deleteEdge(edge.id));
        }
      } else if (this.pastedEdges.length === 1) {
        mutationManager.deleteEdge(this.pastedEdges[0].id);
      }
      
      if (this.pastedNodes.length > 1) {
        if (mutationManager.batchDeleteNodes) {
          mutationManager.batchDeleteNodes(this.pastedNodes.map(n => n.id));
        } else {
          this.pastedNodes.forEach((node) => mutationManager.deleteNode(node.id));
        }
      } else if (this.pastedNodes.length === 1) {
        mutationManager.deleteNode(this.pastedNodes[0].id);
      }
    }
    
    store.setSelected(new Set());
  }

  redo(): void {
    this.execute();
  }
}
